import { useCallback, useRef } from 'react';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { EFeatureFlag } from 'constants/enums';

import {
  collected,
  collectWithBackoff,
} from './useFreeCollectThrottle.utils';
import {
  EFreeCollectOutcome,
  FreeCollectResult,
  IUseFreeCollectThrottleReturn,
} from './useFreeCollectThrottle.types';

/**
 * Guards free-offer collects against backend throttling.
 *
 * With the flag on, a create-order request rejected as throttled (HTTP 429) is
 * retried silently using a 2/4/6/8/10 seconds backoff, while repeating a collect
 * for an offer that is still in-flight is skipped and logged as a console
 * warning. With the flag off, the collect proceeds without any throttling.
 *
 * @example
 * const { runThrottledCollect } = useFreeCollectThrottle();
 * const result = await runThrottledCollect(offerId, () =>
 *   API.createOrder.mutateAsync(freeOrderData),
 * );
 * if (result.outcome === EFreeCollectOutcome.SKIPPED) return;
 * const orderId = result.response?.data?.orderId;
 */
export const useFreeCollectThrottle = (): IUseFreeCollectThrottleReturn => {
  const isEnabled = useFeatureFlag(
    EFeatureFlag.STORE_ENABLE_FREE_COLLECTION_THROTTLE,
  );
  const inFlightOffers = useRef<Set<string>>(new Set());

  const runThrottledCollect = useCallback(
    async <T>(
      offerId: string,
      performCollect: () => Promise<T>,
    ): Promise<FreeCollectResult<T>> => {
      if (!isEnabled) return collected(await performCollect());
      if (inFlightOffers.current.has(offerId)) {
        console.warn(
          `[FreeCollectThrottle] still processing previous collect for offer ${offerId}`,
        );
        return { outcome: EFreeCollectOutcome.SKIPPED };
      }
      inFlightOffers.current.add(offerId);
      try {
        return collected(await collectWithBackoff(performCollect));
      } finally {
        inFlightOffers.current.delete(offerId);
      }
    },
    [isEnabled],
  );

  return { runThrottledCollect };
};
